import { useState, useEffect } from "react";
import { Crown, Copy, Gift, Users, ExternalLink } from "lucide-react";
import { apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ReferralCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [codeRes, statsRes] = await Promise.all([
        apiCall("GET", "/referral/my-code", null, token),
        apiCall("GET", "/referral/my-stats", null, token),
      ]);
      if (codeRes.success && codeRes.data.referral_code) {
        setData({
          code: codeRes.data.referral_code,
          count: codeRes.data.referral_count || 0,
          ...(statsRes.success ? statsRes.data : {}),
        });
      }
      setLoading(false);
    };
    load();
  }, [token]);

  if (loading || !data?.code) return null;

  const shareUrl = `${window.location.origin}/pricing?ref=${data.code}`;
  const shareMsg = `Join me on SIWON HARMONY for life & relationship wisdom coaching! Use my referral code ${data.code} to get a special discount. Sign up here: ${shareUrl}`;

  const copyCode = () => {
    navigator.clipboard.writeText(data.code);
    toast.success("Referral code copied!");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Referral link copied!");
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareMsg)}`, "_blank");
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMsg)}`, "_blank");
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent("Join SIWON HARMONY — Special Referral Offer!")}&body=${encodeURIComponent(shareMsg)}`, "_blank");

  const unusedRewards = (data.reward_coupons || []).filter(c => c.used_count === 0 && new Date(c.valid_until) > new Date());

  return (
    <div className="mt-2 rounded-xl border border-divine-gold/15 bg-gradient-to-r from-divine-gold/5 to-amber-500/5 overflow-hidden" data-testid="referral-card">
      <div className="px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-divine-gold/10 flex items-center justify-center shrink-0">
            <Gift className="w-4.5 h-4.5 text-divine-gold" />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-sm text-divine-gold tracking-wide">Share & Earn</p>
            <p className="text-chakra-crown/40 text-xs">Refer friends, both get rewards</p>
          </div>
        </div>

        {/* Code + Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="font-mono text-sm bg-cosmic-void/60 px-3 py-1.5 rounded-lg border border-divine-gold/20 text-divine-gold tracking-wider" data-testid="referral-code-display">
            {data.code}
          </span>
          <button onClick={copyCode}
            className="p-1.5 rounded-lg bg-divine-gold/10 text-divine-gold hover:bg-divine-gold/20 transition-all"
            title="Copy code" data-testid="copy-referral-code">
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button onClick={copyLink}
            className="p-1.5 rounded-lg bg-divine-gold/10 text-divine-gold hover:bg-divine-gold/20 transition-all"
            title="Copy share link" data-testid="copy-referral-link">
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
          <span className="w-px h-5 bg-divine-gold/15 mx-0.5" />
          <button onClick={shareWhatsApp}
            className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all"
            title="Share via WhatsApp" data-testid="share-whatsapp">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </button>
          <button onClick={shareTwitter}
            className="p-1.5 rounded-lg bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-all"
            title="Share on X / Twitter" data-testid="share-twitter">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </button>
          <button onClick={shareEmail}
            className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all"
            title="Share via Email" data-testid="share-email">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
          </button>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-center">
            <p className="font-heading text-lg text-emerald-400" data-testid="referral-count">{data.total_referrals || data.count || 0}</p>
            <p className="text-chakra-crown/40 text-xs">Referred</p>
          </div>
          {unusedRewards.length > 0 && (
            <div className="text-center">
              <p className="font-heading text-lg text-amber-400">{unusedRewards.length}</p>
              <p className="text-chakra-crown/40 text-xs">Rewards</p>
            </div>
          )}
        </div>
      </div>

      {/* Reward Coupons */}
      {unusedRewards.length > 0 && (
        <div className="px-4 pb-3 border-t border-divine-gold/10 pt-2">
          <p className="text-xs text-chakra-crown/50 mb-1.5">Your reward coupons:</p>
          <div className="flex gap-2 flex-wrap">
            {unusedRewards.map(c => (
              <span key={c.coupon_id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs">
                <Crown className="w-3 h-3 text-emerald-400" />
                <span className="font-mono text-emerald-400">{c.code}</span>
                <span className="text-chakra-crown/40">({c.discount_type === "percentage" ? `${c.discount_value}% off` : `${c.discount_value} off`})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralCard;
